import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { toast } from 'react-toastify';

export default function AddArea() {
  // Add Form States
  const [areaName, setAreaName] = useState('');
  const [tableName, setTableName] = useState('');
  const [tableCount, setTableCount] = useState('');
  // Optional area-manager details.
  const [managerName, setManagerName] = useState('');
  const [managerMobile, setManagerMobile] = useState('');
  const [incentive, setIncentive] = useState('');

  // List & Edit States
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingArea, setEditingArea] = useState(null);

  const company = JSON.parse(sessionStorage.getItem('companyDetails'));
  const token = sessionStorage.getItem('token');
  const companyId = company?.id;

  // ================== FETCH AREAS ==================
  const fetchAreas = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/home/GetAllArea/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = Array.isArray(res.data) ? res.data : [];
      setAreas(data);
      console.log('Areas Data:', data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load areas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAreas();
  }, [companyId]);

  // ================== ADD AREA ==================
  const saveArea = async () => {
    // #4: Table Name (prefix) is now OPTIONAL — leave blank to auto-name from
    // the area (e.g. Garden → G-01, Family Hall → FH-01).
    if (!areaName || !tableCount) {
      toast.error('Area name and table count are required');
      return;
    }

    const formData = new FormData();
    formData.append('AreaName', areaName);
    formData.append('TableName', tableName);
    formData.append('TableCount', tableCount);
    formData.append('Companyid', companyId);
    if (managerName.trim()) formData.append('ManagerName', managerName.trim());
    if (managerMobile.trim())
      formData.append('ManagerMobile', managerMobile.trim());
    if (incentive !== '' && !isNaN(Number(incentive)))
      formData.append('Incentive', Number(incentive));

    try {
      await axios.post(
        `${config.API_BASE_URL}/home/AddResturentarea`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast.success('Area added successfully!');
      setAreaName('');
      setTableName('');
      setTableCount('');
      setManagerName('');
      setManagerMobile('');
      setIncentive('');
      fetchAreas();
    } catch (err) {
      console.error(err);
      toast.error('Failed to add area');
    }
  };

  // ================== EDIT ==================
  const handleEditClick = (area) => {
    // Keep the original name + count to detect changes on save (#4).
    setEditingArea({
      ...area,
      _origAreaName: area.areaName,
      _origTableCount: area.tableCount,
    });
    setOpenEdit(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingArea((prev) => ({ ...prev, [name]: value }));
  };

  const updateArea = async () => {
    if (!editingArea?.areaName || !editingArea?.tableCount) {
      toast.error('Area name and table count are required');
      return;
    }

    // #4: if the area NAME changed, ask whether to rename all its tables.
    // Tables are only touched when the admin says YES (protects running orders).
    const nameChanged =
      (editingArea.areaName || '').trim() !==
      (editingArea._origAreaName || '').trim();
    const countChanged =
      Number(editingArea.tableCount) !== Number(editingArea._origTableCount);
    // Count change always regenerates (to add/remove tables). A name-only
    // change asks first (protects manual names + running orders).
    let renameTables = countChanged;
    if (nameChanged) {
      const yes = window.confirm(
        'Area name changed.\n\n' +
          'Rename all tables in this area to match the new name?\n\n' +
          'OK = Yes, regenerate table names (e.g. G-01, G-02)\n' +
          'Cancel = No, keep the current table names',
      );
      renameTables = yes || countChanged;
    }

    try {
      await axios.post(
        `${config.API_BASE_URL}/home/UpdateArea`,
        { ...editingArea, RenameTables: renameTables },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      toast.success('Area updated successfully!');
      setOpenEdit(false);
      fetchAreas();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update area');
    }
  };

  return (
    <Container maxWidth='lg' sx={{ mt: 8 }}>
      {/* Add Form */}
      <Card sx={{ borderRadius: 4, p: 3, boxShadow: 4, mb: 5 }}>
        <CardContent>
          <Typography
            variant='h5'
            align='center'
            sx={{ mb: 3, fontWeight: 700 }}>
            Add Area
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box display='flex' flexDirection='column' gap={2}>
            <TextField
              label='Area Name'
              fullWidth
              value={areaName}
              onChange={(e) => setAreaName(e.target.value)}
            />
            <TextField
              label='Table Name / Prefix (optional)'
              fullWidth
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              helperText='Leave blank to auto-name from area (Garden → G-01, Family Hall → FH-01)'
            />
            <TextField
              label='Table Count'
              type='number'
              fullWidth
              value={tableCount}
              onChange={(e) => setTableCount(e.target.value)}
            />
            <TextField
              label='Manager Name (optional)'
              fullWidth
              value={managerName}
              onChange={(e) => setManagerName(e.target.value)}
            />
            <TextField
              label='Manager Mobile (optional)'
              fullWidth
              value={managerMobile}
              onChange={(e) =>
                setManagerMobile(e.target.value.replace(/\D/g, '').slice(0, 10))
              }
            />
            <TextField
              label='Incentive / Commission (optional)'
              type='number'
              fullWidth
              value={incentive}
              onChange={(e) => setIncentive(e.target.value)}
            />

            <Button
              variant='contained'
              size='large'
              onClick={saveArea}
              sx={{ mt: 2 }}>
              Save Area
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Area List */}
      <Card sx={{ borderRadius: 4, p: 3, boxShadow: 4 }}>
        <CardContent>
          <Typography variant='h5' sx={{ mb: 3, fontWeight: 700 }}>
            Area List
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>ID</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Area Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Table Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Table Count</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Manager</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Mobile</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Incentive</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell align='center'>
                    <strong>Action</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align='center'>
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : areas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align='center'>
                      No areas found
                    </TableCell>
                  </TableRow>
                ) : (
                  areas.map((area) => (
                    <TableRow key={area.id}>
                      <TableCell>{area.id}</TableCell>
                      <TableCell>{area.areaName}</TableCell>
                      <TableCell>{area.tableName}</TableCell>
                      <TableCell>{area.tableCount}</TableCell>
                      <TableCell>{area.managerName || '-'}</TableCell>
                      <TableCell>{area.managerMobile || '-'}</TableCell>
                      <TableCell>
                        {area.incentive != null && area.incentive !== ''
                          ? area.incentive
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography
                          color={area.isActive ? 'success.main' : 'error.main'}
                          fontWeight={500}>
                          {area.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <IconButton
                          color='primary'
                          onClick={() => handleEditClick(area)}>
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>Edit Area</DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2} sx={{ mt: 2 }}>
            <TextField
              label='Area Name'
              fullWidth
              name='areaName'
              value={editingArea?.areaName || ''}
              onChange={handleEditChange}
            />
            <TextField
              label='Table Name'
              fullWidth
              name='tableName'
              value={editingArea?.tableName || ''}
              onChange={handleEditChange}
            />
            <TextField
              label='Table Count'
              type='number'
              fullWidth
              name='tableCount'
              value={editingArea?.tableCount || ''}
              onChange={handleEditChange}
            />
            <TextField
              label='Manager Name (optional)'
              fullWidth
              name='managerName'
              value={editingArea?.managerName || ''}
              onChange={handleEditChange}
            />
            <TextField
              label='Manager Mobile (optional)'
              fullWidth
              name='managerMobile'
              value={editingArea?.managerMobile || ''}
              onChange={handleEditChange}
            />
            <TextField
              label='Incentive / Commission (optional)'
              type='number'
              fullWidth
              name='incentive'
              value={editingArea?.incentive ?? ''}
              onChange={handleEditChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} color='inherit'>
            Cancel
          </Button>
          <Button onClick={updateArea} variant='contained'>
            Update Area
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
